import React, { useState, useEffect, useCallback, useMemo } from "react";
import { loadState } from "../../stores/store";
import {
  searchGroups,
  deleteGroup as deleteGroupApi,
  getGroupById,
  updateGroup,
  addContestantToGroup,
  updateGroupRound,
} from "../../api/group";
import { searchContestants } from "../../api/contestant";
import { createApplication } from "../../api/application";
import { getGroupEvents } from "../../api/event";
import { PlusOutlined, SearchOutlined, CloseOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Flex,
  Space,
  Typography,
  message,
  Select,
  Input,
} from "antd";
import GroupTable from "../../components/Groups/groupTable";
import CreateGroupModal from "../../components/Groups/createGroupModal";
import GroupMembersModal from "../../components/Groups/groupMembersModal";
import GroupPhotoUploadModal from "../../components/Groups/groupPhotoUploadModal";
import InvoiceDrawer from "../../components/Invoices/InvoiceDrawer/invoiceDrawer";
import EditGroupModal from "../../components/Groups/editGroupModal";
import JoinGroupModal from "../../components/Groups/joinGroupModal";
import ApplyEventsModal from "../../components/Applications/applyEventsModal";
import GroupApplicationsModal from "../../components/Groups/groupApplicationsModal";
import "./groups.scss";
import { openNotificationWithIcon } from "../../utils/notificationUtils";
import { useSettings } from "../../contexts/settingsContext";
import { fetchProvinces } from "../../api/competition";
import { exportGroups } from "../../utils/exportGroupUtils";

const { Title } = Typography;

const Groups = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [contestants, setContestants] = useState([]);
  const [events, setEvents] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupForInvoice, setSelectedGroupForInvoice] = useState(null);

  const [isCreateGroupModalVisible, setIsCreateGroupModalVisible] =
    useState(false);
  const [isEventPopupVisible, setIsEventPopupVisible] = useState(false);
  const [isGroupMembersModalVisible, setIsGroupMembersModalVisible] =
    useState(false);
  const [isEditGroupModalVisible, setIsEditGroupModalVisible] =
    useState(false);
  const [isJoinGroupModalVisible, setIsJoinGroupModalVisible] =
    useState(false);
  const [isGroupApplicationsModalVisible, setIsGroupApplicationsModalVisible] =
    useState(false);

  // inside Groups component state
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [selectedGroupDetails, setSelectedGroupDetails] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [groupCreated, setGroupCreated] = useState(false);
  const [selectedGroupCode, setSelectedGroupCode] = useState(null);

  const [editGroupLoading, setEditGroupLoading] = useState(false);

  const [groupPhotoModalVisible, setGroupPhotoModalVisible] = useState(false);
  const [photoModalGroup, setPhotoModalGroup] = useState(null);

  const [provinces, setProvinces] = useState([]);
  const [activeProvinces, setActiveProvinces] = useState([]);
  const [roundFilter, setRoundFilter] = useState(null);
  const [provinceFilter, setProvinceFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [applications, setApplications] = useState([]);

  const { state: settings } = useSettings();
  const state = loadState();
  const user = state.user;
  const { role, _id: currentUserId } = user;

  const fetchGroups = useCallback(
    async (pageOverride, pageSizeOverride) => {
      const page = pageOverride ?? currentPage;
      const size = pageSizeOverride ?? pageSize;

      try {
        setLoading(true);
        const result = await searchGroups({
          role,
          page,
          pageSize: size,
          round: roundFilter,
          search: searchQuery,
          province: provinceFilter || undefined,
        });

        if (result && Array.isArray(result.groups)) {
          setGroups(result.groups);
          setTotalGroups(result.total || 0);
        } else {
          setGroups([]);
          setTotalGroups(0);
        }
      } catch (err) {
        console.error("Error fetching groups", err);
        setGroups([]);
        setTotalGroups(0);
      } finally {
        setLoading(false);
      }
    },
    [role, roundFilter, searchQuery, provinceFilter, currentPage, pageSize]
  );

  useEffect(() => {
    fetchGroups(currentPage, pageSize);
  }, [fetchGroups, currentPage, pageSize]);

  const handleRowClick = useCallback(
    (groupId) => {
      const group = groups.find((g) => g._id === groupId);
      setSelectedGroupForInvoice(group || null);
    },
    [groups]
  );

  const loadProvinces = useCallback(async () => {
    try {
      const competitionId =
        settings?.selectedCompetition?._id || settings?.selectedCompetitionId;

      if (!competitionId) {
        setProvinces([]);
        setActiveProvinces([]);
        return;
      }

      const data = await fetchProvinces(competitionId);
      setProvinces(data || []);

      const now = new Date();
      const active = (data || []).filter((prov) => {
        if (!prov.date) return true;
        const d = new Date(prov.date);
        return d >= now;
      });
      setActiveProvinces(active);
    } catch (error) {
      console.error("Error loading provinces", error);
      setProvinces([]);
      setActiveProvinces([]);
    }
  }, [settings]);

  useEffect(() => {
    loadProvinces();
  }, [loadProvinces]);

  const selectableProvinces = useMemo(() => {
    if (role === "admin") {
      return provinces || [];
    }
    return activeProvinces || [];
  }, [role, provinces, activeProvinces]);

  const fetchContestants = useCallback(async () => {
    try {
      const response = await searchContestants();
      const data = response?.data || response || [];

      const activeProvince = user.activeProvince;
      const filteredContestants = Array.isArray(data)
        ? data.filter(
            (contestant) =>
              contestant.province === activeProvince ||
              contestant.province?._id === activeProvince
          )
        : [];

      setContestants(filteredContestants);
    } catch (err) {
      console.error("Error fetching contestants", err);
      setContestants([]);
    }
  }, [user.activeProvince]);

  const fetchEvents = useCallback(async () => {
    try {
      const data = await getGroupEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching events", error);
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const openCreateGroupModal = useCallback(() => {
    fetchContestants();
    setIsCreateGroupModalVisible(true);
  }, [fetchContestants]);

  const openJoinGroupPopup = useCallback(() => {
    setIsJoinGroupModalVisible(true);
  }, []);

  const handleGroupCreated = useCallback(
    async (newGroup) => {
      try {
        setGroups((prevGroups) => [newGroup, ...prevGroups]);
        setTotalGroups((prev) => prev + 1);
        setSelectedGroup(newGroup);
        setSelectedGroupCode(newGroup.group_code);
        setIsCreateGroupModalVisible(false);
        setIsEventPopupVisible(true);
        setGroupCreated(true);
        setGroupPhotoModalVisible(true);
        setPhotoModalGroup(newGroup);
        fetchEvents();
      } catch (error) {
        console.error("Error handling group creation", error);
      }
    },
    [fetchEvents]
  );

  const handleGroupApplicationsViewClick = useCallback((group) => {
    setSelectedGroup(group);
    setIsGroupApplicationsModalVisible(true);
  }, []);

  const handleApplyToEventsForNewGroup = useCallback(async () => {
    try {
      if (!selectedGroup) {
        throw new Error("Group must be selected");
      }

      if (!settings.singleRound && selectedGroup.round === 1) {
        const maxEvents = selectedGroup.group_size < 10 ? 4 : 2;
        if (selectedEvents.length > maxEvents) {
          throw new Error(
            `You can only apply this group to a maximum of ${maxEvents} events for round 1.`
          );
        }
      }

      const applicationPromises = selectedEvents.map((eventId) => {
        const applicationPayload = {
          event: eventId,
          group: selectedGroup._id,
          created_by: user._id,
          province: selectedGroup.province,
        };
        return createApplication(applicationPayload);
      });

      await Promise.all(applicationPromises);

      openNotificationWithIcon(
        "success",
        "Application Created",
        "The group has been successfully applied to the selected events."
      );
      fetchGroups();
      setIsEventPopupVisible(false);
      setSelectedEvents([]);
      setGroupCreated(false);
    } catch (error) {
      openNotificationWithIcon(
        "error",
        "Error",
        `Error applying to events: ${error.message || error}`
      );
    }
  }, [selectedEvents, selectedGroup, user._id, fetchGroups]);

const openGroupMembersModal = useCallback(async (group) => {
  try {
    const updatedGroup = await getGroupById(group._id);
    if (updatedGroup && updatedGroup.group) {
      setSelectedGroupMembers(updatedGroup.group);
      setSelectedGroupDetails(updatedGroup); // store full group
    } else {
      setSelectedGroupMembers([]);
      setSelectedGroupDetails(updatedGroup || null);
    }
    setIsGroupMembersModalVisible(true);
  } catch (error) {
    console.error("Error fetching group members", error);
  }
}, []);


  const handleEditGroupSave = useCallback(
    async (groupId, newMembers) => {
      try {
        setEditGroupLoading(true);
        const formattedMembers = newMembers.map((memberId) => ({
          group_member: memberId,
        }));
        const updatedGroup = await updateGroup(groupId, {
          group: formattedMembers,
        });

        setGroups((prevGroups) =>
          prevGroups.map((group) =>
            group._id === groupId ? updatedGroup : group
          )
        );

        openNotificationWithIcon(
          "success",
          "Group Updated",
          "The group has been successfully updated."
        );
        setIsEditGroupModalVisible(false);
      } catch (error) {
        console.error("Error updating group members", error);
        openNotificationWithIcon("error", "Error", error.message);
      } finally {
        setEditGroupLoading(false);
      }
    },
    []
  );

    const handleModalToggle = useCallback(() => {
    setIsCreateGroupModalVisible(false);
    setIsEventPopupVisible(false);
    setIsGroupMembersModalVisible(false);
    setIsJoinGroupModalVisible(false);
    setIsGroupApplicationsModalVisible(false);
    setIsEditGroupModalVisible(false);
    setSelectedEvents([]);
    setSelectedGroup(null);
    setSelectedGroupDetails(null);
    }, []);

  const handleExportGroups = async () => {
    await exportGroups(groups, user.activeProvince);
  };

  const deleteGroup = useCallback(async (groupId) => {
    try {
      await deleteGroupApi(groupId);
      setGroups((prevGroups) =>
        prevGroups.filter((group) => group._id !== groupId)
      );
      setTotalGroups((prev) => Math.max(prev - 1, 0));
      openNotificationWithIcon(
        "success",
        "Group Deleted",
        "The group has been successfully deleted."
      );
    } catch (error) {
      openNotificationWithIcon(
        "error",
        "Error",
        `Error deleting group: ${error}`
      );
    }
  }, []);

  const handleJoinGroup = useCallback(
    async (groupCode) => {
      try {
        const response = await searchContestants();
        const data = response?.data || response || [];
        const userContestants = Array.isArray(data) ? data : [];

        if (userContestants.length === 0) {
          throw new Error("No contestant found for the current user");
        }
        const contestantId = userContestants[0]._id;
        await addContestantToGroup(groupCode, contestantId);
        openNotificationWithIcon(
          "success",
          "Group Joined",
          "You have successfully joined the group."
        );
        setIsJoinGroupModalVisible(false);
        fetchGroups();
      } catch (error) {
        openNotificationWithIcon(
          "error",
          "Error",
          `Error joining group: ${error.message}`
        );
      }
    },
    [fetchGroups]
  );

  const handleSearchInputChange = (e) => setSearchTerm(e.target.value);

  const handleSearchEnter = (e) => {
    if (e.key === "Enter") {
      setSearchQuery(searchTerm);
      setCurrentPage(1);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const refetchCurrentPage = useCallback(
    () => fetchGroups(currentPage, pageSize),
    [fetchGroups, currentPage, pageSize]
  );

  return (
    <div>
      <Flex
        style={{ marginBottom: "15px" }}
        className="dashboard_page_header_area"
        align="center"
        justify="space-between"
      >
        <Title
          className="dashboard_page_title"
          level={4}
          style={{ margin: 0 }}
        >
          Group Entries
        </Title>

        {settings.selectedRound === 1 && (
          <Space className="dashboard_page_title_actions">
            {role === "admin" && (
              <Button type="primary" onClick={handleExportGroups}>
                Export Group Entries
              </Button>
            )}
            <Button type="primary" onClick={openCreateGroupModal}>
              <PlusOutlined />
              Create Group
            </Button>
            {role === "contestant" && (
              <Button type="default" onClick={openJoinGroupPopup}>
                Join Group
              </Button>
            )}
          </Space>
        )}
      </Flex>

      <Space style={{ marginBottom: 16 }} wrap>
        <span>Filter by Round:</span>
        <Select
          value={roundFilter !== null ? roundFilter : ""}
          onChange={(value) => {
            setRoundFilter(value === "" ? null : Number(value));
            setCurrentPage(1);
          }}
          style={{ width: 120, marginRight: 16 }}
          options={[
            { value: "", label: "All" },
            { value: 1, label: "1" },
            { value: 2, label: "2" },
            { value: 3, label: "3" },
          ]}
        />

        <span>Filter by Audition Date:</span>
        <Select
          value={provinceFilter}
          onChange={(value) => {
            setProvinceFilter(value || "");
            setCurrentPage(1);
          }}
          style={{ width: isMobile ? "100%" : 400 }}
          showSearch
          allowClear
          placeholder="Select a province"
        >
          <Select.Option value="">All</Select.Option>
          {provinces &&
            Array.isArray(provinces) &&
            provinces.map((prov) => (
              <Select.Option key={prov._id} value={prov._id}>
                {prov.province} ({prov.location}) - {prov.date}
              </Select.Option>
            ))}
        </Select>
      </Space>

      <Card bordered={false}>
        <Input
          placeholder="Search by group name, teacher name, or member name"
          value={searchTerm}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchEnter}
          style={{ marginBottom: 16, width: "100%" }}
          suffix={
            <>
              <SearchOutlined style={{ marginRight: "8px" }} />
              {searchTerm && (
                <CloseOutlined
                  onClick={handleClearSearch}
                  style={{ cursor: "pointer" }}
                />
              )}
            </>
          }
        />

        <GroupTable
          groups={groups}
          loading={loading}
          openGroupMembersModal={openGroupMembersModal}
          deleteGroup={deleteGroup}
          role={role}
          currentUserId={currentUserId}
          events={events}
          handleGroupApplicationsViewClick={handleGroupApplicationsViewClick}
          handleRowClick={handleRowClick}
          onGroupRoundUpdated={refetchCurrentPage}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalGroups,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      </Card>

      {selectedGroupForInvoice && (
        <InvoiceDrawer
          group={selectedGroupForInvoice}
          source="group"
          onRequestClose={() => setSelectedGroupForInvoice(null)}
          fetchData={refetchCurrentPage}
        />
      )}

      <CreateGroupModal
        visible={isCreateGroupModalVisible}
        onClose={handleModalToggle}
        onGroupCreated={handleGroupCreated}
        contestants={contestants}
        provinces={selectableProvinces}
      />

      <ApplyEventsModal
        visible={isEventPopupVisible}
        onClose={handleModalToggle}
        events={events}
        loading={loading}
        openPopup={() => {}}
        userRole={role}
        handleDeleteEvent={() => {}}
        selectedEvents={selectedEvents}
        onApply={handleApplyToEventsForNewGroup}
      />

      {selectedGroup && (
        <GroupApplicationsModal
          visible={isGroupApplicationsModalVisible}
          group={selectedGroup}
          onCancel={handleModalToggle}
        />
      )}

        <GroupMembersModal
        visible={isGroupMembersModalVisible}
        onClose={handleModalToggle}
        groupMembers={selectedGroupMembers}
        group={selectedGroupDetails}
        role={role}
        competitionId={settings?.selectedCompetition?._id || settings?.selectedCompetitionId}
        onGroupUpdated={(updatedGroup) => {
          setSelectedGroupDetails(updatedGroup);
          setSelectedGroupMembers(updatedGroup.group || []);
          setGroups((prev) =>
            prev.map((g) => (g._id === updatedGroup._id ? { ...g, ...updatedGroup } : g))
          );
        }}
        />


      <JoinGroupModal
        visible={isJoinGroupModalVisible}
        onClose={handleModalToggle}
        onJoinGroup={handleJoinGroup}
      />

      <EditGroupModal
        visible={isEditGroupModalVisible}
        onClose={() => setIsEditGroupModalVisible(false)}
        onSave={handleEditGroupSave}
        loading={editGroupLoading}
      />

      <GroupPhotoUploadModal
        visible={groupPhotoModalVisible}
        onClose={() => setGroupPhotoModalVisible(false)}
        group={photoModalGroup}
      />
    </div>
  );
};

export default Groups;
